import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { NewSessionWire } from './models/tuna.model';
import { Observable } from 'rxjs';

/**
 * Google Pay Response Interfaces
 */
interface GooglePayTokenizationData {
  token: string;
  type: string;
}

interface GooglePayInfo {
  cardDetails: string;
  cardNetwork: string;
}

interface GooglePayMethodData {
  description: string;
  info: GooglePayInfo;
  tokenizationData: GooglePayTokenizationData;
  type: string;
}

interface GooglePayResponse {
  apiVersion: number;
  apiVersionMinor: number;
  paymentMethodData: GooglePayMethodData;
}

/**
 * Apple Pay Response Interfaces
 */
interface ApplePayPaymentData {
  data: string;
  signature: string;
  header: {
    publicKeyHash: string;
    ephemeralPublicKey: string;
    transactionId: string;
  };
  version: string;
}

interface ApplePayPaymentMethod {
  displayName: string;
  network: string;
  type: string;
}

interface ApplePayToken {
  paymentData: ApplePayPaymentData;
  paymentMethod: ApplePayPaymentMethod;
  transactionIdentifier: string;
}

interface ApplePayDetails {
  token: ApplePayToken;
}

interface WebPaymentAPIResponse {
  requestId: string;
  methodName: string;
  details: ApplePayDetails;
}

/**
 * Wallet payload type that handles various formats from Tuna
 */
type WalletPayload = GooglePayResponse | WebPaymentAPIResponse | string | {
  Token?: string;
  statusCode?: string;
  status?: string;
  [key: string]: any;
};

/**
 * Service for handling Tuna wallet integrations (Apple Pay and Google Pay)
 * 
 * @description
 * This service manages the integration with Tuna's payment SDK for digital wallets.
 * It handles token extraction and formatting according to Tuna's requirements:
 * - Apple Pay: Extracts only the paymentData object as a JSON string
 * - Google Pay: Extracts the tokenization data token
 */
@Injectable({ providedIn: 'root' })
export class TunaWalletService {
  private tunaInstance: any;

  constructor() {}

  /**
   * Initialize Tuna SDK instance
   * @param sessionId - The session ID from Tuna
   * @returns The Tuna instance
   */
  initTuna(sessionId: string): any {
    if (!(window as any).Tuna) {
      throw new Error('Tuna.js not loaded');
    }
    
    this.tunaInstance = (window as any).Tuna(sessionId, environment.tunaEnv as 'sandbox' | 'production');
    return this.tunaInstance;
  }

  /**
   * Mount Google Pay button with Tuna SDK
   * @param containerId - The HTML element ID where the button will be mounted
   * @param amount - The payment amount in BRL
   * @param onSuccess - Callback function called with the extracted token
   * @returns Promise<boolean> - True if button mounted successfully
   */
  async mountGooglePay(containerId: string, amount: number, onSuccess: (token: string) => void): Promise<boolean> {
    try {
      if (!this.tunaInstance) {
        console.error('[TunaWalletService] Tuna instance not initialized');
        return false;
      }
      console.log('[Wallet][mountGooglePay] container:', containerId, '| amount recebido:', amount, '| totalPrice (toFixed):', amount.toFixed(2));

      this.tunaInstance.useGooglePay(
        containerId,
        {
          merchantName: 'Yooga',
          merchantId: environment.tunaGoogleMerchantId
        },
        (payload: any) => {
          const token = this.extractToken(payload);
          if (token) {
            onSuccess(token);
          } else {
            console.error('[TunaWalletService] Failed to extract Google Pay token');
          }
        },
        null, // validationGroup (optional)
        {
          totalPriceStatus: 'FINAL',
          totalPrice: amount.toFixed(2),
          currencyCode: 'BRL'
        },
        {
          buttonColor: 'white',
          buttonType: 'pay',
          buttonRadius: 5,
          buttonLocale: 'pt',
          buttonHeight: '48px',
          buttonWidth: '100%',
          buttonSizeMode: 'fill'
        }
      );

      // useGooglePay não informa se deu certo: o isReadyToPay dele roda depois e
      // pode falhar (sem cartão, contexto inseguro, país não suportado) sem
      // lançar nada aqui. Só o botão no DOM prova que montou.
      const mounted = await this.waitForMountedButton(containerId);
      if (!mounted) {
        console.warn('[TunaWalletService] Google Pay não montou o botão em', containerId);
      }
      return mounted;
    } catch (error) {
      console.error('[TunaWalletService] Google Pay setup error:', error);
      return false;
    }
  }

  /** Aguarda o SDK injetar o botão no container. */
  private async waitForMountedButton(containerId: string, timeoutMs = 3000): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      if (document.querySelector(`${containerId} button`)) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return false;
  }

  /**
   * Mount Apple Pay button with Tuna SDK
   * @param containerId - The HTML element ID where the button will be mounted
   * @param amount - The payment amount in BRL
   * @param onSuccess - Callback function called with the extracted token
   * @returns Promise<boolean> - True if button mounted successfully
   */
  async mountApplePay(containerId: string, amount: number, onSuccess: (token: string) => void): Promise<boolean> {
    if (!this.tunaInstance) {
      console.error('[TunaWalletService] Tuna instance not initialized');
      return false;
    }
    console.log('[Wallet][mountApplePay] container:', containerId, '| amount recebido:', amount, '| value (toFixed):', amount.toFixed(2));

    try {
      const applePaySettings = {
        buttom: {
          selector: containerId,
          style: {
            buttonStyle: 'white-outline',
            type: 'check-out',
            locale: 'pt-BR'
          }
        },
        merchantId: environment.tunaAppleMerchantId,
        displayName: 'checkout.yooga',
        applePayCallback: (applePayPayload: any) => this.handleApplePayCallback(applePayPayload, onSuccess),
        cart: {
          total: {
            label: 'Total',
            amount: {
              currency: 'BRL',
              value: amount.toFixed(2)
            }
          }
        }
      };

      const isApplePayReady = await this.tunaInstance.useApplePay(applePaySettings);
      return isApplePayReady;
    } catch (error) {
      console.error('[TunaWalletService] Apple Pay setup error:', error);
      return false;
    }
  }

  /**
   * Handle Apple Pay callback from Tuna
   * @private
   */
  private handleApplePayCallback(applePayPayload: any, onSuccess: (token: string) => void): boolean {
    console.log('[TunaWalletService] 🍎 Apple Pay callback RECEBIDO');
    console.log('[TunaWalletService] Payload type:', typeof applePayPayload);
    console.log('[TunaWalletService] Payload keys:', applePayPayload ? Object.keys(applePayPayload) : 'null');
    console.log('[TunaWalletService] Payload completo:', JSON.stringify(applePayPayload, null, 2));
    
    try {
      let paymentData: string | null = null;
      
      // Handle PaymentResponse object (Web Payment API)
      if (applePayPayload && applePayPayload.details && applePayPayload.details.token && applePayPayload.details.token.paymentData) {
        console.log('[TunaWalletService] ✅ Formato: PaymentResponse object (Web Payment API)');
        paymentData = JSON.stringify(applePayPayload.details.token.paymentData);
      }
      // Handle direct JSON string (Web Payment API response)
      else if (typeof applePayPayload === 'string') {
        console.log('[TunaWalletService] ✅ Formato: JSON string');
        paymentData = this.extractApplePayDataFromString(applePayPayload);
      }
      // Handle wrapped format with Token field
      else if (applePayPayload && applePayPayload.Token && typeof applePayPayload.Token === 'string') {
        console.log('[TunaWalletService] ✅ Formato: Token field wrapper');
        paymentData = this.extractApplePayDataFromString(applePayPayload.Token);
      }
      // Try to use the payload directly as token (fallback)
      else if (applePayPayload && typeof applePayPayload === 'object') {
        console.log('[TunaWalletService] ⚠️ Formato desconhecido - tentando usar payload direto');
        paymentData = JSON.stringify(applePayPayload);
      }
      
      if (paymentData) {
        console.log('[TunaWalletService] ✅ Token extraído com sucesso! Length:', paymentData.length);
        console.log('[TunaWalletService] Primeiros 100 chars:', paymentData.substring(0, 100));
        onSuccess(paymentData);
        return true;
      }
      
      console.error('[TunaWalletService] ❌ Falhou ao extrair Apple Pay token - payload não reconhecido');
      console.error('[TunaWalletService] Tentando callback com payload original como fallback...');
      
      // Last resort: try with stringified payload
      try {
        const fallbackToken = JSON.stringify(applePayPayload);
        console.log('[TunaWalletService] Fallback token:', fallbackToken.substring(0, 100));
        onSuccess(fallbackToken);
        return true;
      } catch (e) {
        console.error('[TunaWalletService] Fallback também falhou:', e);
        return false;
      }
      
    } catch (error) {
      console.error('[TunaWalletService] ❌ Erro crítico ao processar Apple Pay token:', error);
      return false;
    }
  }

  /**
   * Extract Apple Pay payment data from JSON string
   * @private
   */
  private extractApplePayDataFromString(jsonString: string): string | null {
    try {
      const parsed = JSON.parse(jsonString);
      
      // Extract paymentData from Web Payment API structure
      if (parsed.details && parsed.details.token && parsed.details.token.paymentData) {
        return JSON.stringify(parsed.details.token.paymentData);
      }
      
      return null;
    } catch (error) {
      console.error('[TunaWalletService] Failed to parse Apple Pay JSON:', error);
      return null;
    }
  }

  /**
   * Extract token from wallet payment payloads
   * For Apple Pay: extracts only paymentData as required by Tuna
   * For Google Pay: extracts the tokenization data token
   */
  private extractToken(payload: WalletPayload): string | null {
    if (!payload) {
      return null;
    }
    
    // Handle cancellation
    if (typeof payload === 'object' && payload !== null && 'statusCode' in payload && payload.statusCode === 'CANCELED') {
      return null;
    }
    if (typeof payload === 'object' && payload !== null && 'status' in payload && payload.status === 'CANCELED') {
      return null;
    }
    
    // Handle Apple Pay - might come as direct Web Payment API response string
    if (typeof payload === 'string') {
      try {
        const parsed = JSON.parse(payload);
        
        // Web Payment API structure - extract only paymentData for Tuna
        if (parsed.details && parsed.details.token && parsed.details.token.paymentData) {
          return JSON.stringify(parsed.details.token.paymentData);
        }
        
        // Google Pay format
        if (parsed.paymentMethodData && parsed.paymentMethodData.tokenizationData && 
            parsed.paymentMethodData.tokenizationData.token) {
          return parsed.paymentMethodData.tokenizationData.token;
        }
      } catch (error) {
        // If parsing fails, return the string as-is (might be a raw token)
        return payload;
      }
    }
    
    // Handle Apple Pay Token field from Tuna (if it comes wrapped)
    if (typeof payload === 'object' && payload !== null && 'Token' in payload && payload.Token && typeof payload.Token === 'string') {
      try {
        const parsed = JSON.parse(payload.Token);
        
        // Web Payment API structure - extract only paymentData for Tuna
        if (parsed.details && parsed.details.token && parsed.details.token.paymentData) {
          return JSON.stringify(parsed.details.token.paymentData);
        }
      } catch (error) {
        console.error('[TunaWalletService] Failed to parse Token field:', error);
      }
    }
    
    // Handle Google Pay format
    if (typeof payload === 'object' && payload !== null && 'paymentMethodData' in payload && payload.paymentMethodData && 
        payload.paymentMethodData.tokenizationData && payload.paymentMethodData.tokenizationData.token) {
      return payload.paymentMethodData.tokenizationData.token;
    }
    
    console.error('[TunaWalletService] Could not extract token from payload:', payload);
    return null;
  }
}